/**
 * Código de traspaso: lo que contiene el QR y lo que el usuario copia/pega.
 *
 * Formato: `FCHK1-<CODIGO_SALA>-<CLAVE_base64url>`
 *  - `FCHK1`: prefijo + versión (permite evolucionar el formato).
 *  - `CODIGO_SALA`: código corto de la sala de señalización (solo id público).
 *  - `CLAVE`: clave AES-256 one-time en base64url — NUNCA viaja al servidor;
 *    solo existe en el QR/portapapeles entre los dos dispositivos.
 */

import { base64UrlToBytes, bytesToBase64Url } from "./encoding";
import { TRANSFER_KEY_BYTES } from "./crypto";

export const TRANSFER_CODE_PREFIX = "FCHK1";

export interface ParsedTransferCode {
  roomCode: string;
  keyBytes: Uint8Array;
}

export function buildTransferCode(roomCode: string, keyBytes: Uint8Array): string {
  if (keyBytes.length !== TRANSFER_KEY_BYTES) {
    throw new Error("Invalid transfer key length");
  }
  if (!/^[A-Z2-9]{4,16}$/u.test(roomCode)) {
    throw new Error("Invalid room code");
  }
  return `${TRANSFER_CODE_PREFIX}-${roomCode}-${bytesToBase64Url(keyBytes)}`;
}

/** Devuelve `null` si el texto no es un código de traspaso válido. */
export function parseTransferCode(raw: string): ParsedTransferCode | null {
  const trimmed = raw.trim();
  const match = /^FCHK1-([A-Za-z2-9]{4,16})-([A-Za-z0-9_-]{43})$/u.exec(trimmed);
  if (!match) {
    return null;
  }
  let keyBytes: Uint8Array;
  try {
    keyBytes = base64UrlToBytes(match[2]!);
  } catch {
    return null;
  }
  if (keyBytes.length !== TRANSFER_KEY_BYTES) {
    return null;
  }
  return { roomCode: match[1]!.toUpperCase(), keyBytes };
}
